import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { FileText } from "lucide-react";
import type { ClaimCandidateGroupMember, ClaimGroupValueRollup, ClaimValueSource } from "@hp/types";
import type { AssistantSource } from "@hp/api-client";
import { Badge, Card, CardBody, CardHeader, CardTitle } from "@hp/ui";
import { ClaimStatusPill } from "../ClaimStatusPill";
import { ClaimValueTableCells } from "./ClaimValueTableCells";
import { ClaimGroupValueDisplay } from "./ClaimGroupValueDisplay";
import { SourceDocumentViewer } from "../SourceDocumentViewer";
import { formatINR, humanizeIssueTag } from "../../lib/workbenchFormat";

interface ClaimGroupDetailPanelProps {
  groupMembers: ClaimCandidateGroupMember[];
  groupValueRollup: ClaimGroupValueRollup | null;
  valueSources: ClaimValueSource[];
}

export function ClaimGroupDetailPanel({
  groupMembers,
  groupValueRollup,
  valueSources,
}: ClaimGroupDetailPanelProps) {
  const [viewerSource, setViewerSource] = React.useState<AssistantSource | null>(null);
  const location = useLocation();
  // Same rule as ClaimDetailPage's own appeal links: stay inside the popup shell when already in it.
  const inPopup = location.pathname.startsWith("/popup/");
  const disputeThreadPath = (findingId: string) =>
    inPopup ? `/popup/findings/${findingId}/dispute-thread` : `/findings/${findingId}/dispute-thread`;
  const sortedMembers = [...groupMembers].sort((a, b) => (a.financial_year ?? "").localeCompare(b.financial_year ?? ""));

  if (groupMembers.length <= 1 && valueSources.length === 0) {
    return null;
  }

  const rollup = groupValueRollup ?? {
    principal_low: 0,
    principal_high: 0,
    carrying_cost_low: 0,
    carrying_cost_high: 0,
    total_low: 0,
    total_high: 0,
    member_count: groupMembers.length,
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            Claim breakdown ({rollup.member_count} {rollup.member_count === 1 ? "tranche" : "FY tranches"})
          </CardTitle>
          <p className="mt-1 text-xs text-slate-500">
            Each row is one financial year in the same recoverable pot. Totals below are summed across all tranches.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          {groupValueRollup ? (
            <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-brand-700">Portfolio total</p>
              <div className="mt-2">
                <ClaimGroupValueDisplay rollup={groupValueRollup} />
              </div>
            </div>
          ) : null}

          {sortedMembers.length > 1 ? (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 font-medium">FY</th>
                    <th className="px-4 py-2.5 font-medium">Source order</th>
                    <th className="px-4 py-2.5 font-medium">Principal</th>
                    <th className="px-4 py-2.5 font-medium">LPS</th>
                    <th className="px-4 py-2.5 font-medium">Total</th>
                    <th className="px-4 py-2.5 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3 font-mono text-xs tabular-nums text-slate-700">
                        {member.financial_year ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        {member.finding_id ? (
                          <Link
                            to={disputeThreadPath(member.finding_id)}
                            state={{ backTo: location.pathname, backLabel: "Claim Detail" }}
                            className="font-mono text-xs font-medium tabular-nums text-brand-700 hover:underline"
                          >
                            {member.finding_case_number ?? member.finding_id.slice(0, 8)}
                          </Link>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                        <div className="text-[0.6875rem] text-slate-400">{humanizeIssueTag(member.issue_tag)}</div>
                      </td>
                      <ClaimValueTableCells
                        valueLow={member.value_low ?? 0}
                        valueHigh={member.value_high ?? 0}
                        breakdown={member.value_breakdown}
                      />
                      <td className="px-4 py-3">
                        {member.status ? (
                          <ClaimStatusPill status={member.status} counselValidatedAt={member.counsel_validated_at} />
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t border-slate-200 bg-slate-50 text-sm">
                  <tr>
                    <td className="px-4 py-3 font-medium text-slate-700" colSpan={2}>Total</td>
                    <td className="px-4 py-3 font-mono tabular-nums font-medium text-slate-900">
                      {formatINR(rollup.principal_high)}
                    </td>
                    <td className="px-4 py-3 font-mono tabular-nums text-slate-600">
                      {rollup.carrying_cost_high > 0 ? formatINR(rollup.carrying_cost_high) : "—"}
                    </td>
                    <td className="px-4 py-3 font-mono tabular-nums font-semibold text-slate-900">
                      {formatINR(rollup.total_high)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : null}
        </CardBody>
      </Card>

      {valueSources.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Supporting documents & ledger</CardTitle>
            <p className="mt-1 text-xs text-slate-500">
              Regulatory orders and tariff ledger rows that back the amounts above.
            </p>
          </CardHeader>
          <CardBody>
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {valueSources.map((source) => (
                <li key={`${source.kind}-${source.finding_id ?? source.order_ref ?? source.label}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={source.kind === "regulatory_order" ? "brand" : "info"}>
                        {source.kind === "regulatory_order" ? "Order" : "Ledger"}
                      </Badge>
                      {source.financial_year ? (
                        <span className="font-mono text-xs tabular-nums text-slate-500">{source.financial_year}</span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-sm text-slate-800">{source.label}</p>
                    {source.amount_claimed != null || source.amount_allowed != null ? (
                      <p className="mt-0.5 text-xs text-slate-500">
                        {source.amount_claimed != null ? `Claimed ${formatINR(source.amount_claimed)}` : null}
                        {source.amount_claimed != null && source.amount_allowed != null ? " · " : null}
                        {source.amount_allowed != null ? `Allowed ${formatINR(source.amount_allowed)}` : null}
                      </p>
                    ) : null}
                    {source.source_document_key ? (
                      <p className="mt-0.5 truncate text-[0.6875rem] text-slate-400">{source.source_document_key}</p>
                    ) : null}
                  </div>
                  {source.finding_id ? (
                    <button
                      type="button"
                      onClick={() =>
                        setViewerSource({
                          type: "finding",
                          id: source.finding_id!,
                          label: source.finding_case_number ?? source.label,
                        })
                      }
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50"
                    >
                      <FileText className="h-3.5 w-3.5" aria-hidden />
                      View PDF
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {viewerSource ? <SourceDocumentViewer source={viewerSource} onClose={() => setViewerSource(null)} /> : null}
    </>
  );
}
